// HistoryGroups (story 211, RN5, RN6, RN9): a «close now» and its trim are ONE correction of two steps. Sun does not give the
// children of a close (D-S8 of #210): only the trim carries `parentCorrectionId`, and from the close to its trim there is only
// the blocker REVERT_CHILD_FIRST while the trim is still applied (V3, V4). So the screen groups them inside the page it got
// and nothing is ever deduced from times, machines or ids (02 «Políticas» › Agrupación).

const CHILD_FIRST = 'DATA_CORRECTION_REVERT_CHILD_FIRST'

/**
 * RN5: what a header is. `CLOSE` = step 1 «Close now»; `TRIM` / `VOID_STEP` = step 2 («Trim» / «Void») of the close
 * `parentCorrectionId`; `SINGLE` = a correction of one step, as before.
 */
export const stepOf = (header) => {
  if (header.kind === 'CLOSE_NOW') return 'CLOSE'
  if (header.parentCorrectionId) return header.kind === 'VOID' ? 'VOID_STEP' : 'TRIM'
  return 'SINGLE'
}

export const isClose = (header) => stepOf(header) === 'CLOSE'
export const isStep = (header) => ['TRIM', 'VOID_STEP'].includes(stepOf(header))

/**
 * RN6 [D-S3]: the items of a page, in Sun's order. A step 2 and its close in the same page are ONE item at the position of the
 * first of them; a step 2 without its close, or a close without its step, is an item of its own; any other header is `single`.
 *   { key, close?, step?, single? }
 */
export const itemsOf = (content) => {
  const used = new Set()
  const items = []
  content.forEach((header) => {
    if (used.has(header.id)) return
    used.add(header.id)
    const step = stepOf(header)
    if (step === 'CLOSE') {
      const child = content.find((candidate) => !used.has(candidate.id) && isStep(candidate) && candidate.parentCorrectionId === header.id)
      if (child) used.add(child.id)
      items.push(child ? { key: child.id, close: header, step: child } : { key: header.id, close: header })
    } else if (step === 'SINGLE') {
      items.push({ key: header.id, single: header })
    } else {
      const parent = content.find((candidate) => !used.has(candidate.id) && isClose(candidate) && candidate.id === header.parentCorrectionId)
      if (parent) used.add(parent.id)
      items.push(parent ? { key: header.id, close: parent, step: header } : { key: header.id, step: header })
    }
  })
  return items
}

/** The header whose «what», imputed time, progress and mini bars an item shows: the trim of a group, else the only one. */
export const primaryOf = (item) => item.step || item.single || item.close

/** The ids an item stands for. */
export const idsOf = (item) => [item.close, item.step, item.single].filter(Boolean).map((header) => header.id)

/** 03 HistoryItem.contains: the item is the selected one whether the chosen id is its close or its step 2. */
export const containsId = (item, id) => Boolean(id) && idsOf(item).includes(id)

/** The steps of an item as chips: `CLOSE`, `TRIM` or `VOID_STEP` (none for a single correction). */
export const stepsOf = (item) => {
  if (item.single) return []
  const second = item.step ? [stepOf(item.step)] : []
  return ['CLOSE', ...second]
}

/**
 * RN7: the status of the group. Both applied ⇒ APPLIED; both reverted ⇒ REVERTED; the close applied and the trim reverted ⇒
 * TRIM_REVERTED; an item alone shows its own.
 */
export const groupStatusOf = ({ close, step, single }) => {
  if (single) return single.status
  if (close && step) {
    if (close.status === 'APPLIED' && step.status === 'REVERTED') return 'TRIM_REVERTED'
    if (close.status === step.status) return close.status
    return step.status
  }
  return (close || step).status
}

/** The id of the trim a close points to through REVERT_CHILD_FIRST (only while the trim is applied, V3), '' if none. */
export const childFirstIdOf = (detail) => {
  const blockers = (detail.revertibility && detail.revertibility.blockers) || []
  const blocker = blockers.find((candidate) => candidate.message === CHILD_FIRST)
  const ids = blocker && blocker.details && blocker.details.correctionIds
  return ids && ids.length > 0 ? ids[0] : ''
}

/**
 * RN9: the other step of a chosen correction. A step 2 ⇒ its `parentCorrectionId`; a close ⇒ the step of its item of the page
 * or, if not, `correctionIds[0]` of its REVERT_CHILD_FIRST; anything else (or a close without that blocker) ⇒ ''.
 */
export const partnerIdOf = (header, knownPartnerId = '') => {
  if (isStep(header)) return header.parentCorrectionId
  if (isClose(header)) return knownPartnerId || (header.revertibility ? childFirstIdOf(header) : '')
  return ''
}

/** The id the list hands over when an item is chosen, and the other step it already knows. */
export const selectionOf = (item) => {
  const chosen = primaryOf(item)
  const partner = item.close && item.step ? (chosen.id === item.step.id ? item.close.id : item.step.id) : ''
  return { id: chosen.id, partnerId: partner }
}
